export interface ExpectedPayment {
  residentName: string;
  funder: string;
  expected: number;
  source: string;
  period: string;
}

export interface ReceivedPayment {
  name: string;
  amount: number;
  reference: string;
}

export interface ReconRow {
  residentName: string;
  funder: string;
  expected: number;
  received: number;
  difference: number;
  status: string;
  confirmed: boolean;
  confirmedAt: string;
}

export function normalizeName(name: string): string {
  return String(name || "").toLowerCase().replace(/[^a-z ]/g, "").replace(/\s+/g, " ").trim();
}

export function parseAmount(value: any): number {
  if (typeof value === "number") return value;
  const cleaned = String(value || "").replace(/[^0-9.-]/g, "");
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
}

export function nameSimilarity(a: string, b: string): number {
  const na = normalizeName(a);
  const nb = normalizeName(b);

  if (!na || !nb) return 0;
  if (na === nb) return 1;

  const ta = na.split(" ");
  const tb = nb.split(" ");
  const overlap = ta.filter(word => word.length > 2 && tb.includes(word)).length;

  return overlap / Math.max(ta.length, tb.length);
}

export function parseRemittanceSheet(rows: any[]): ReceivedPayment[] {
  const received: ReceivedPayment[] = [];

  rows.forEach(row => {
    const keys = Object.keys(row);
    const nameKey = keys.find(k => /name|resident|client|service/i.test(k));
    const amountKey = keys.find(k => /amount|paid|value|total|payment/i.test(k));
    const refKey = keys.find(k => /ref|invoice|period|date/i.test(k));

    const name = nameKey ? String(row[nameKey]) : "";
    const amount = amountKey ? parseAmount(row[amountKey]) : 0;

    if (name && amount) {
      received.push({ name, amount, reference: refKey ? String(row[refKey]) : "" });
    }
  });

  return received;
}

export function parseRemittanceText(text: string): ReceivedPayment[] {
  const received: ReceivedPayment[] = [];

  String(text || "").split(/\n/).forEach(line => {
    const amountMatch = line.match(/£?\s?([0-9][0-9,]*\.?[0-9]{0,2})/);
    const amount = amountMatch ? parseAmount(amountMatch[1]) : 0;
    const name = line.replace(/£?\s?[0-9][0-9,]*\.?[0-9]{0,2}/g, "").replace(/[,;|]/g, " ").trim();

    if (name && amount) received.push({ name, amount, reference: "" });
  });

  return received;
}

export function reconcile(expected: ExpectedPayment[], received: ReceivedPayment[]): ReconRow[] {
  const rows: ReconRow[] = [];

  expected.forEach(exp => {
    const match = received
      .map(rec => ({ rec, score: nameSimilarity(exp.residentName, rec.name) }))
      .filter(item => item.score >= 0.4)
      .sort((a, b) => b.score - a.score)[0];

    const receivedAmount = match ? match.rec.amount : 0;
    const difference = receivedAmount - exp.expected;

    let status = "NOT PAID";
    if (match && Math.abs(difference) < 0.01) status = "PAID IN FULL";
    else if (match && difference < 0) status = "UNDERPAID";
    else if (match && difference > 0) status = "OVERPAID";

    rows.push({
      residentName: exp.residentName,
      funder: exp.funder,
      expected: exp.expected,
      received: receivedAmount,
      difference,
      status,
      confirmed: false,
      confirmedAt: "",
    });
  });

  return rows;
}

export interface ReconSummary {
  totalExpected: number;
  totalReceived: number;
  outstanding: number;
  paidCount: number;
  notPaidCount: number;
  byFunder: Record<string, { expected: number; received: number; count: number }>;
}

export function summarize(rows: ReconRow[]): ReconSummary {
  const byFunder: Record<string, { expected: number; received: number; count: number }> = {};

  let totalExpected = 0;
  let totalReceived = 0;
  let paidCount = 0;
  let notPaidCount = 0;

  rows.forEach(row => {
    totalExpected += row.expected;
    totalReceived += row.received;

    if (row.status === "PAID IN FULL") paidCount += 1;
    if (row.status === "NOT PAID" || row.status === "UNDERPAID") notPaidCount += 1;

    if (!byFunder[row.funder]) byFunder[row.funder] = { expected: 0, received: 0, count: 0 };
    byFunder[row.funder].expected += row.expected;
    byFunder[row.funder].received += row.received;
    byFunder[row.funder].count += 1;
  });

  return { totalExpected, totalReceived, outstanding: totalExpected - totalReceived, paidCount, notPaidCount, byFunder };
}